import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { albumService } from '../../services/api';
import { stickerCode } from '../albumData';

/**
 * Dropdown que vive en un portal en <body> para escapar el `overflow-y-auto`
 * del panel derecho del álbum (que clipearía cualquier `position: absolute`).
 * Calcula su posición a partir del bounding rect del trigger.
 */
function PortalDropdown({ anchorRef, open, onClose, width = 260, children }) {
  const [coords, setCoords] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    setCoords({ top: rect.bottom + 8, right });
  }, [open, anchorRef]);
  useEffect(() => {
    if (!open) return;
    const onScroll = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, anchorRef]);
  if (!open || !coords) return null;
  return createPortal(
    <div
      style={{ position: 'fixed', top: coords.top, right: coords.right, width, zIndex: 1000 }}
      className="bg-surface-container rounded-2xl shadow-xl border border-outline-variant/20 p-1.5 max-h-[360px] overflow-y-auto"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}

/**
 * Sección "Ofertas Figus": browse server-driven de ofertas OPEN visibles para
 * el usuario, con búsqueda (por número/prefijo de código de figurita o
 * publicador), filtro por comunidad y ordenamiento. Paginación incremental
 * ("Cargar más").
 *
 * Toda la lógica de filtrado/búsqueda/ordenamiento vive en el backend: el
 * componente solo manda el texto crudo (q) y el backend resuelve si es
 * número, prefijo de código (e.g. "RSA" → todas las RSA) o nombre de
 * publicador. Esto deja la fuente de verdad de los códigos en el server.
 */
function OffersBrowserSection({ communities, currentUserId, onOpenOffer, embedded = false }) {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [communityId, setCommunityId] = useState(''); // '' = todas
  const [sortBy, setSortBy] = useState('newest');
  const [includeMine, setIncludeMine] = useState(false);

  const [communityOpen, setCommunityOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const communityRef = useRef(null);
  const sortRef = useRef(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const PAGE_SIZE = 12;
  const reqIdRef = useRef(0);

  // Debounce de la búsqueda: 300ms para no saturar el backend al tipear.
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(tid);
  }, [search]);

  // Cierra dropdowns al click fuera, igual patrón que el home.
  useEffect(() => {
    const onMouseDown = (e) => {
      if (communityRef.current && !communityRef.current.contains(e.target)) setCommunityOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const fetchPage = useCallback(async (nextOffset, append) => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await albumService.browseOffers({
        q: debouncedSearch || undefined,
        communityId: communityId ? Number(communityId) : undefined,
        sortBy,
        limit: PAGE_SIZE,
        offset: nextOffset,
        excludeMine: !includeMine,
      });
      // Defensive guard: ignore stale responses if a newer fetch fired.
      if (myReqId !== reqIdRef.current) return;
      const data = res?.data?.data ?? res?.data ?? {};
      const newItems = Array.isArray(data.items) ? data.items : [];
      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setTotal(typeof data.total === 'number' ? data.total : newItems.length);
      setHasMore(Boolean(data.hasMore));
      setOffset(nextOffset + newItems.length);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      setError(t('No hay ofertas que coincidan'));
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [debouncedSearch, communityId, sortBy, includeMine, t]);

  // Refetch desde 0 cada vez que cambia un filtro/sort/búsqueda.
  useEffect(() => {
    fetchPage(0, false);
  }, [fetchPage]);

  const onLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    fetchPage(offset, true);
  }, [loading, hasMore, offset, fetchPage]);

  const safeCommunities = useMemo(
    () => Array.isArray(communities) ? communities : [],
    [communities]
  );

  const SORT_OPTIONS = useMemo(() => [
    { value: 'newest',         label: t('Más nuevas') },
    { value: 'oldest',         label: t('Más antiguas') },
    { value: 'most_stickers',  label: t('Más figuritas') },
  ], [t]);

  const selectedCommunity = safeCommunities.find(c => String(c.id) === String(communityId));
  const communityLabel = selectedCommunity ? selectedCommunity.name : t('Comunidades');
  const sortLabel = SORT_OPTIONS.find(o => o.value === sortBy)?.label ?? t('Ordenar por');
  const hasActiveFilters = !!debouncedSearch || !!communityId || sortBy !== 'newest' || includeMine;

  const openComm = () => { setCommunityOpen(o => !o); setSortOpen(false); };
  const openSort = () => { setSortOpen(o => !o); setCommunityOpen(false); };
  const clearAll = () => {
    setSearch('');
    setCommunityId('');
    setSortBy('newest');
    setIncludeMine(false);
  };

  // Pill base + estado activo/inactivo: copia de los tokens del home para que
  // el toolbar se sienta idéntico al search bar del Dashboard.
  const pillBase = 'flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full cursor-pointer transition-colors whitespace-nowrap select-none';
  const pillActive = 'bg-primary/20 backdrop-blur-md text-primary border border-primary/40';
  const pillInactive = 'bg-white/50 backdrop-blur-md text-on-surface-variant border border-black/[0.06] hover:bg-white/70';

  return (
    <section
      className={embedded
        ? 'bg-surface'
        : 'bg-surface border border-outline rounded-xl overflow-hidden'}
      aria-label={t('Ofertas Figus')}
    >
      {/* Header con título y meta. En embedded omitimos el título (el tab ya lo provee). */}
      {!embedded && (
        <header className="px-5 py-4 flex flex-col mobile-md:flex-row mobile-md:items-end gap-3 border-b border-outline-variant/30">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-black text-on-surface tracking-tight">
              {t('Ofertas Figus')}
            </h2>
            <p className="text-xs text-on-surface-variant mt-0.5">
              {loading
                ? t('Cargando ofertas…')
                : t('Mostrando {{count}} de {{total}}', { count: items.length, total })}
            </p>
          </div>
        </header>
      )}

      {/* Toolbar tipo glass: replica el search bar del home (Dashboard.js).
          En embedded, search ocupa fila propia y los pills wrappean abajo (siempre columna). */}
      <div className={embedded ? 'px-3 py-3' : 'px-5 py-4'}>
        <div className={`flex flex-col ${embedded ? '' : 'tablet-sm:flex-row tablet-sm:items-stretch'} gap-2 bg-white/80 backdrop-blur-lg rounded-2xl p-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04] border border-black/[0.06] transition-all duration-300 focus-within:shadow-[0_8px_32px_rgba(37,99,235,0.18),0_2px_8px_rgba(0,0,0,0.10)] focus-within:ring-2 focus-within:ring-primary/40`}>
          {/* Search */}
          <div className="flex flex-1 items-center px-3 gap-3 pb-1.5">
            <span className="material-symbols-outlined text-on-surface-variant/70 text-[20px] shrink-0">search</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={embedded ? t('Buscar figurita o usuario…') : t('Buscar por figurita o publicador…')}
              className="bg-transparent border-none focus:ring-0 focus:outline-none w-full min-w-0 text-sm font-medium placeholder:text-on-surface-variant/50 text-on-surface"
              aria-label={t('Buscar')}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="shrink-0 hover:opacity-60"
                aria-label={t('Limpiar')}
              >
                <span className="material-symbols-outlined text-[16px] text-on-surface-variant/60">close</span>
              </button>
            )}
          </div>

          {/* Divider horizontal between search and pills in embedded */}
          {embedded && <div className="mx-3 border-t border-black/[0.06]" />}

          {/* Pills: comunidad / orden / incluir mías / clear */}
          <div className="flex items-center gap-1.5 px-1 flex-wrap">
            {/* Comunidad */}
            <div className="relative" ref={communityRef}>
              <div
                onClick={openComm}
                className={`${pillBase} ${communityId ? pillActive : pillInactive}`}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={communityOpen}
              >
                <span className="material-symbols-outlined text-sm">group</span>
                <span className="max-w-[140px] truncate">{communityLabel}</span>
                <span className="material-symbols-outlined text-[14px]">expand_more</span>
              </div>
              <PortalDropdown anchorRef={communityRef} open={communityOpen} onClose={() => setCommunityOpen(false)} width={260}>
                <button
                  type="button"
                  onClick={() => { setCommunityId(''); setCommunityOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-[13px] rounded-xl transition-colors text-on-surface ${!communityId ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}
                >
                  {t('Todas las comunidades')}
                </button>
                {safeCommunities.length > 0 && <div className="mx-3 my-1.5 border-t border-outline-variant/15" />}
                {safeCommunities.map(c => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => { setCommunityId(String(c.id)); setCommunityOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-[13px] rounded-xl transition-colors text-on-surface ${String(communityId) === String(c.id) ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}
                  >
                    {c.name}
                  </button>
                ))}
              </PortalDropdown>
            </div>

            {/* Orden */}
            <div className="relative" ref={sortRef}>
              <div
                onClick={openSort}
                className={`${pillBase} ${sortBy !== 'newest' ? pillActive : pillInactive}`}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
              >
                <span className="material-symbols-outlined text-sm">swap_vert</span>
                <span>{sortLabel}</span>
                <span className="material-symbols-outlined text-[14px]">expand_more</span>
              </div>
              <PortalDropdown anchorRef={sortRef} open={sortOpen} onClose={() => setSortOpen(false)} width={200}>
                {SORT_OPTIONS.map(opt => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-[13px] rounded-xl transition-colors text-on-surface ${sortBy === opt.value ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </PortalDropdown>
            </div>

            {/* Incluir mías (toggle pill) */}
            <button
              type="button"
              onClick={() => setIncludeMine(v => !v)}
              className={`${pillBase} ${includeMine ? pillActive : pillInactive}`}
              aria-pressed={includeMine}
            >
              <span className="material-symbols-outlined text-sm">{includeMine ? 'check_circle' : 'add_circle'}</span>
              <span>{t('Incluir mías')}</span>
            </button>

            {/* Reset */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAll}
                className={`${pillBase} text-error hover:bg-error/10`}
                aria-label={t('Limpiar filtros')}
                title={t('Limpiar filtros')}
              >
                <span className="material-symbols-outlined text-sm">restart_alt</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="divide-y divide-outline-variant/10">
        {loading && items.length === 0 ? (
          <LoadingRows />
        ) : items.length === 0 ? (
          <EmptyState message={error || t('No hay ofertas que coincidan')} />
        ) : (
          items.map(offer => (
            <OfferBrowseRow
              key={offer.id}
              offer={offer}
              isMine={offer.userId === currentUserId}
              onClick={() => onOpenOffer?.(offer)}
            />
          ))
        )}
      </div>

      {hasMore && (
        <div className="px-5 py-4 flex justify-center border-t border-outline-variant/20">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="px-4 py-1.5 text-sm font-bold rounded-lg border border-outline text-on-surface bg-surface hover:bg-surface-container transition-colors disabled:opacity-50"
          >
            {loading ? t('Cargando ofertas…') : t('Cargar más')}
          </button>
        </div>
      )}
    </section>
  );
}

function OfferBrowseRow({ offer, isMine, onClick }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-5 py-3 hover:bg-surface-container transition-colors flex items-center gap-3"
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
        style={{ backgroundColor: colorForUserId(offer.userId) }}
      >
        {(offer.publisherName || '?').slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {!isMine && offer.userId ? (
            <a
              href={`/users/${offer.userId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={t('Ver perfil')}
              className="text-sm font-bold text-on-surface truncate hover:text-primary hover:underline transition-colors"
            >
              {offer.publisherName || `User ${offer.userId}`}
            </a>
          ) : (
            <p className="text-sm font-bold text-on-surface truncate">
              {offer.publisherName || `User ${offer.userId}`}
            </p>
          )}
          {isMine && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-primary/10 text-primary">
              {t('Vos')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs mt-0.5">
          <span className="font-bold" style={{ color: '#15803d' }}>
            {t('Da')} <span className="tabular-nums">{offer.offered?.length || 0}</span>
          </span>
          <span className="text-on-surface-variant">·</span>
          <span className="font-bold" style={{ color: '#b91c1c' }}>
            {t('Pide')} <span className="tabular-nums">{offer.requested?.length || 0}</span>
          </span>
        </div>
        <div className="hidden tablet-sm:flex items-center gap-1 mt-1.5 flex-wrap">
          <StickerPreview nums={offer.offered} accent="#16a34a" />
          <span className="text-on-surface-variant text-[10px] mx-1">↔</span>
          <StickerPreview nums={offer.requested} accent="#b91c1c" />
        </div>
      </div>
      <span className="material-symbols-outlined text-on-surface-variant shrink-0">chevron_right</span>
    </button>
  );
}

function StickerPreview({ nums, accent }) {
  if (!nums || nums.length === 0) return null;
  const visible = nums.slice(0, 5);
  const overflow = nums.length - visible.length;
  return (
    <>
      {visible.map(n => (
        <span
          key={n}
          className="px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          {stickerCode(n)}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] font-bold text-on-surface-variant">+{overflow}</span>
      )}
    </>
  );
}

function LoadingRows({ count = 4 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="px-5 py-3 flex items-center gap-3 animate-pulse">
          <div className="w-10 h-10 rounded-full bg-surface-container-highest shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-surface-container-highest rounded w-1/3" />
            <div className="h-2 bg-surface-container-highest rounded w-1/2" />
          </div>
        </div>
      ))}
    </>
  );
}

function EmptyState({ message }) {
  return (
    <div className="py-12 flex flex-col items-center justify-center text-center px-4">
      <span className="material-symbols-outlined text-4xl text-outline-variant mb-3">
        local_offer
      </span>
      <p className="text-sm font-bold text-on-surface">{message}</p>
    </div>
  );
}

// Color determinístico por userId (mismo enfoque que el resto del album).
function colorForUserId(userId) {
  const palette = ['#2563eb', '#dc2626', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#ca8a04', '#db2777'];
  const idx = Math.abs(Number(userId) || 0) % palette.length;
  return palette[idx];
}

export default OffersBrowserSection;
